import { css } from '@microsoft/fast-element';
import { getToken } from '../../design-tokens';
import { display } from '../../foundation/display';

export const loaderStyles = css`
    ${display('inline-flex')}

    @keyframes dots {
        0% {
            transform: scale(1);
        }
        50% {
            transform: scale(.5);
        }
        100% {
            transform: scale(1);
        }
    }

    :host([appearance=dots]) div {
        --part: 0;
        --total: 0;
        width: calc(${getToken('space-design-unit')} * 3px);
        height: calc(${getToken('space-design-unit')} * 3px);
        border-radius: calc(${getToken('space-design-unit')} * 2px);
        background-color: currentColor;
        animation: 1s calc(var(--part) * 125ms) infinite ease-in-out dots;
    }

    :host([appearance=dots]) div + div {
        margin-left: calc(${getToken('space-design-unit')} * 1px);
    }

    @keyframes waves {
        0% {
            opacity: 0.25;
            height: calc(${getToken('space-design-unit')} * 1px);
            margin-top: calc(${getToken('space-design-unit')} * 2.5px);
        }
        50% {
            opacity: 1;
            height: calc(${getToken('space-design-unit')} * 10px);
            margin-top: 0%;
        }
        100% {
            opacity: 0.25;
            height: calc(${getToken('space-design-unit')} * 1px);
            margin-top: calc(${getToken('space-design-unit')} * 2.5px);
        }
    }


    :host([appearance=waves]) {
        display: block;
        height: calc(${getToken('space-design-unit')} * 10px);
    }

    :host([appearance=waves]) div {
        --half: round(down, var(--total) / 2);
        float: left;
        background: ${getToken('color-primary-base')};
        width: calc(${getToken('space-design-unit')} * 2px);
        height: calc(${getToken('space-design-unit')} * 1px);
        border-radius: calc(${getToken('space-design-unit')} * 2px);
        margin-top: calc(${getToken('space-design-unit')} * 2.5px);
        animation: 2.5s 0ms infinite linear waves;
    }

    :host([appearance=waves]) div:nth-child(1) { animation-delay: -1.4s; }
    :host([appearance=waves]) div:nth-child(2) { animation-delay: -1.2s; }
    :host([appearance=waves]) div:nth-child(3) { animation-delay: -1.0s; }
    :host([appearance=waves]) div:nth-child(4) { animation-delay: -0.8s; }
    :host([appearance=waves]) div:nth-child(5) { animation-delay: -0.6s; }
    :host([appearance=waves]) div:nth-child(6) { animation-delay: -0.4s; }
    :host([appearance=waves]) div:nth-child(7) { animation-delay: -0.2s; }
    :host([appearance=waves]) div:nth-child(8) { animation-delay: 0s; }
    :host([appearance=waves]) div:nth-child(9) { animation-delay: -0.2s; }
    :host([appearance=waves]) div:nth-child(10) { animation-delay: -0.4s; }
    :host([appearance=waves]) div:nth-child(11) { animation-delay: -0.6s; }
    :host([appearance=waves]) div:nth-child(12) { animation-delay: -0.8s; }
    :host([appearance=waves]) div:nth-child(13) { animation-delay: -1.0s; }
    :host([appearance=waves]) div:nth-child(14) { animation-delay: -1.2s; }
    :host([appearance=waves]) div:nth-child(15) { animation-delay: -1.4s; }

    :host([appearance=waves]) div + div {
        margin-left: calc(${getToken('space-design-unit')} * 1px);
    }

    @keyframes spinner1 {
        0% { opacity: 0; }
        12.5% { opacity: 1; }
        100% { opacity: 1; }
    }

    @keyframes spinner2 {
        0%, 12.5% { opacity: 0; }
        25% { opacity: 1; }
        100% { opacity: 1; }
    }

    @keyframes spinner3 {
        0%, 25% { opacity: 0; }
        37.5% { opacity: 1; }
        100% { opacity: 1; }
    }

    @keyframes spinner4 {
        0%, 37.5% { opacity: 0; }
        50% { opacity: 1; }
        100% { opacity: 1; }
    }

    @keyframes spinner5 {
        0%, 50% { opacity: 0; }
        62.5% { opacity: 1; }
        100% { opacity: 1; }
    }

    @keyframes spinner6 {
        0%, 62.5% { opacity: 0; }
        75% { opacity: 1; }
        100% { opacity: 1; }
    }

    @keyframes spinner7 {
        0%, 75% { opacity: 0; }
        87.5% { opacity: 1; }
        100% { opacity: 1; }
    }

    @keyframes spinner8 {
        0%, 87.5% { opacity: 0; }
        100% { opacity: 1; }
    }

    :host([appearance=spinner]) {
        display: block;
        position: relative;
        width: calc(${getToken('space-design-unit')} * 18px);
        height: calc(${getToken('space-design-unit')} * 18px);
    }

    :host([appearance=spinner]) div {
        position: absolute;
        width: calc(${getToken('space-design-unit')} * 1px);
        height: calc(${getToken('space-design-unit')} * 5px);
        background: ${getToken('color-primary-base')};
        top: calc(${getToken('space-design-unit')} * 4px);
        left: calc(50% - ${getToken('space-design-unit')} * 0.5px);
        transform-origin: center bottom;
    }

    :host([appearance=spinner]) div:nth-child(1) { transform: rotate(0deg) translate(0, -80%); animation: spinner1 2s linear infinite; }
    :host([appearance=spinner]) div:nth-child(2) { transform: rotate(45deg) translate(0, -80%); animation: spinner2 2s linear infinite; }
    :host([appearance=spinner]) div:nth-child(3) { transform: rotate(90deg) translate(0, -80%); animation: spinner3 2s linear infinite; }
    :host([appearance=spinner]) div:nth-child(4) { transform: rotate(135deg) translate(0, -80%); animation: spinner4 2s linear infinite; }
    :host([appearance=spinner]) div:nth-child(5) { transform: rotate(180deg) translate(0, -80%); animation: spinner5 2s linear infinite; }
    :host([appearance=spinner]) div:nth-child(6) { transform: rotate(225deg) translate(0, -80%); animation: spinner6 2s linear infinite; }
    :host([appearance=spinner]) div:nth-child(7) { transform: rotate(270deg) translate(0, -80%); animation: spinner7 2s linear infinite; }
    :host([appearance=spinner]) div:nth-child(8) { transform: rotate(315deg) translate(0, -80%); animation: spinner8 2s linear infinite; }
    `;
